import { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import MediaUpload from "@/components/admin/MediaUpload";

const blank = { title: "", appliance: "", description: "", cover_image: "", video_link: "", media: [], steps: [], order: 0 };

export default function EditGuides() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/guides").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...blank, order: items.length });
  const save = async () => {
    if (!editing.title) { toast.error("Title required"); return; }
    if (editing.id) await api.put(`/guides/${editing.id}`, editing);
    else await api.post("/guides", editing);
    toast.success("Saved"); setEditing(null); load();
  };
  const remove = async (id) => { await api.delete(`/guides/${id}`); toast.success("Deleted"); load(); };

  const setStep = (i, v) => { const s = [...editing.steps]; s[i] = v; setEditing({ ...editing, steps: s }); };
  const addMedia = (d) => setEditing({ ...editing, media: [...editing.media, { url: d.url, type: d.type, caption: "" }] });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><span className="overline text-brand-terracotta">Edit</span><h1 className="font-display text-4xl tracking-tight mt-1">How-To Guides</h1></div>
        <Button onClick={openNew} data-testid="add-guide" className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors"><Plus size={16} className="mr-2" />Add</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} data-testid={`admin-guide-${it.id}`} className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm flex justify-between gap-3">
            <div className="flex gap-3">
              {it.cover_image && <img src={mediaUrl(it.cover_image)} alt="" className="h-14 w-14 rounded-xl object-cover" />}
              <div>
                <span className="overline text-brand-terracotta">{it.appliance}</span>
                <h3 className="font-display text-lg tracking-tight">{it.title}</h3>
                <p className="text-xs text-brand-forest/50">{(it.steps || []).length} steps · {(it.media || []).length} media</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={() => setEditing(it)} data-testid={`edit-guide-${it.id}`} className="text-sm font-medium text-brand-terracotta hover:underline">Edit</button>
              <button onClick={() => remove(it.id)} data-testid={`delete-guide-${it.id}`} className="text-brand-forest/30 hover:text-destructive transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="guide-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Edit" : "New"} Guide</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block">Title</Label><Input data-testid="guide-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Appliance / Tag</Label><Input data-testid="guide-appliance" value={editing.appliance} onChange={(e) => setEditing({ ...editing, appliance: e.target.value })} /></div>
              </div>
              <div><Label className="mb-1.5 block">Description</Label><Textarea data-testid="guide-desc" rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div><Label className="mb-1.5 block">Video Link <span className="text-brand-forest/40">(optional — YouTube, Vimeo, or a direct video URL)</span></Label>
                <Input data-testid="guide-video-link" value={editing.video_link || ""} onChange={(e) => setEditing({ ...editing, video_link: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." />
                {editing.video_link && (
                  <div className="mt-3 flex items-center gap-4 rounded-2xl bg-brand-bg border border-black/5 p-4" data-testid="guide-video-qr">
                    <div className="p-2 bg-white rounded-xl border border-black/5">
                      <QRCodeSVG value={editing.video_link} size={104} fgColor="#2C3B2E" bgColor="#ffffff" level="M" />
                    </div>
                    <div>
                      <p className="font-semibold text-brand-forest">Scan to watch on a phone</p>
                      <p className="text-sm text-brand-forest/60 mt-1">Guests can scan this QR code to open the video directly. Save the guide, then print or place it near the appliance.</p>
                    </div>
                  </div>
                )}
              </div>
              <div><Label className="mb-1.5 block">Cover Image</Label>
                <MediaUpload testid="guide-cover" value={editing.cover_image} onUploaded={(d) => setEditing({ ...editing, cover_image: d.url })} onClear={() => setEditing({ ...editing, cover_image: "" })} />
              </div>

              <div>
                <Label className="mb-1.5 block">Steps</Label>
                <div className="space-y-2">
                  {editing.steps.map((s, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="mt-2.5 h-6 w-6 shrink-0 rounded-full bg-brand-forest text-white text-xs flex items-center justify-center">{i + 1}</span>
                      <Textarea data-testid={`guide-step-${i}`} rows={2} value={s} onChange={(e) => setStep(i, e.target.value)} />
                      <button onClick={() => setEditing({ ...editing, steps: editing.steps.filter((_, x) => x !== i) })} className="mt-2.5 text-brand-forest/30 hover:text-destructive"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setEditing({ ...editing, steps: [...editing.steps, ""] })} data-testid="guide-add-step" className="rounded-full"><Plus size={15} className="mr-1" />Add step</Button>
                </div>
              </div>

              <div>
                <Label className="mb-1.5 block">Photos & Videos</Label>
                <div className="flex flex-wrap gap-3 mb-3">
                  {editing.media.map((m, i) => (
                    <div key={i} className="relative">
                      {m.type === "video"
                        ? <video src={mediaUrl(m.url)} className="h-16 w-24 object-cover rounded-lg bg-black" />
                        : <img src={mediaUrl(m.url)} alt="" className="h-16 w-24 object-cover rounded-lg" />}
                      <button onClick={() => setEditing({ ...editing, media: editing.media.filter((_, x) => x !== i) })} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-white text-xs flex items-center justify-center">×</button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <MediaUpload testid="guide-add-photo" type="image" label="Add photo" onUploaded={addMedia} />
                  <MediaUpload testid="guide-add-video" type="video" label="Add video" onUploaded={addMedia} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="guide-save" className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white"><Save size={16} className="mr-2" />Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
